"use client";

// KPI stat card — a FILLED color plane, not an outline. This is the single
// biggest departure from the flat treatment the dashboard used before: each
// work type's card carries its own tinted background and a border one step of
// the same hue, so Remote / Hybrid / Onsite are three coloured objects rather
// than three identical grey boxes with a 6px dot in front of a label. The hue
// mapping is the one the rest of the app already uses (blue = Remote, amber =
// Hybrid, neutral = the remainder) — see the --tile-* block in globals.css,
// where the dark values are the approved mockup's exact #0d1420/#1e3a5f and
// #1a1608/#4a3a0a.
//
// Four things move here, and they are the spec's, not decorative additions:
//   1. the card fades in and slides up 10px, staggered by list position,
//      on first mount only (`initial`/`animate`, never replayed on re-render);
//   2. the figure counts up from zero with requestAnimationFrame once the
//      card's own entrance has finished (see app/lib/use-count-up.ts);
//   3. hover lifts the card 3px, brightens the border to the accent and drops
//      a soft accent shadow — the mockup's 0 8px 20px -6px rgba(37,99,235,.35);
//   4. everything transitions on the fast token, because all of it is
//      pointer-driven.
//
// The trend line reports a DECREASE as a decrease. The spec names two states —
// green with an up arrow for a positive delta, neutral grey otherwise — and a
// negative delta has no home in "no change"; rendering "-3 this week" as "no
// change" would be a false statement about the data. So the negative branch
// keeps the neutral grey treatment and states the number with a down arrow.

import { motion } from "framer-motion";
import type { ComponentType } from "react";
import { ArrowDownRightIcon, ArrowUpRightIcon } from "@/app/components/icons";
import { DUR_BASE, enterTransition, enterVariants, stagger } from "@/app/components/motion";
import { useCountUp } from "@/app/lib/use-count-up";
import { usePrefersReducedMotion } from "@/app/lib/use-reduced-motion";
import { cn } from "@/app/lib/cn";

export type StatTone = "blue" | "amber" | "neutral";

// One class string per tone: the fill, the border it sits on, the label hue
// (which is the -fg step, the only one that clears AA as 11px text on that
// fill) and the hover border. The hover shadow is shared — one accent glow for
// every tinted card in the app, so the interaction reads as one device.
const TONE: Record<StatTone, string> = {
  blue: "border-tile-blue-border bg-tile-blue-bg text-tile-blue-fg hover:border-tile-hover-border",
  amber:
    "border-tile-amber-border bg-tile-amber-bg text-tile-amber-fg hover:border-tile-hover-border",
  neutral:
    "border-tile-neutral-border bg-tile-neutral-bg text-tile-neutral-fg hover:border-tile-hover-border",
};

// The label icon's badge: a small rounded square tinted one step of the same
// hue as the card (the -fg token at low alpha), so the glyph reads as a badge
// rather than a bare line icon floating in the corner — the landing's
// FeatureIconSquare pattern, scaled to the tinted card.
const TONE_ICON_BADGE: Record<StatTone, string> = {
  blue: "bg-tile-blue-fg/15",
  amber: "bg-tile-amber-fg/15",
  neutral: "bg-tile-neutral-fg/15",
};

export function StatCard({
  label,
  value,
  delta,
  period = "this week",
  icon: Icon,
  tone = "neutral",
  index = 0,
  variant,
  deltaTone = "green",
}: {
  label: string;
  value: number;
  /** Change over `period`. Positive is the only branch that gets color. */
  delta: number;
  period?: string;
  /** Hue of a positive delta's arrow + figure: green (default), or the amber
   * step the approved dashboard mockup gives its Companies card. */
  deltaTone?: "green" | "amber";
  icon: ComponentType<{ className?: string }>;
  tone?: StatTone;
  /** Position in the row — drives the entrance stagger and the count delay. */
  index?: number;
  /** "landing": the marketing mockup's compact KPI — --lp-* tokens and the
   * delta as a green pill in the top-right corner, no trend line. */
  variant?: "landing";
}) {
  const reduced = usePrefersReducedMotion();
  // The figure starts moving only once the card has finished arriving: the
  // entrance's own delay plus its duration.
  const display = useCountUp(value, reduced ? 0 : (stagger(index) + DUR_BASE) * 1000);

  const up = delta > 0;
  const down = delta < 0;
  const deltaColor = deltaTone === "amber" ? "text-warning-text" : "text-success-text";

  if (variant === "landing") {
    return (
      <motion.div
        variants={enterVariants(reduced)}
        initial="hidden"
        animate="visible"
        transition={enterTransition(index, reduced)}
        className="flex flex-col rounded-card border border-lp-border bg-lp-surface p-3.5"
      >
        {/* Label and delta pill share the top row — label left, pill inline at
            the card's top-right — with the figure below, so the pill never
            occupies a row of its own. */}
        <div className="flex items-center justify-between gap-2">
          <p className="flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-lp-ink-2">
            <Icon className="h-3 w-3 flex-none" />
            <span className="truncate">{label}</span>
          </p>
          {delta !== 0 && (
            <span
              className={cn(
                "flex flex-none items-center gap-0.5 rounded-pill px-1.5 py-0.5 text-[10px] font-bold leading-none tabular-nums",
                up ? "bg-lp-pill-green-bg text-lp-pill-green-text" : "bg-lp-pill-blue-bg text-lp-pill-blue-text"
              )}
            >
              {up ? <ArrowUpRightIcon className="h-2.5 w-2.5" /> : <ArrowDownRightIcon className="h-2.5 w-2.5" />}
              {Math.abs(delta)}%
            </span>
          )}
        </div>
        <p
          className={cn(
            "text-[24px] font-bold leading-none tracking-tight text-lp-ink tabular-nums",
            "mt-2.5"
          )}
        >
          {display}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={enterVariants(reduced)}
      initial="hidden"
      animate="visible"
      transition={enterTransition(index, reduced)}
      className={cn(
        "flex flex-col rounded-card border p-4",
        "transition-[transform,border-color,box-shadow] duration-150 ease-out",
        "hover:-translate-y-[3px] hover:shadow-[var(--tile-hover-shadow)]",
        "motion-reduce:transition-none motion-reduce:hover:transform-none",
        TONE[tone]
      )}
    >
      <p className="flex items-center gap-1.5 text-[11px] font-medium">
        <span
          className={cn(
            "flex h-6 w-6 flex-none items-center justify-center rounded-button",
            TONE_ICON_BADGE[tone]
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        {label}
      </p>

      <p className="mt-2.5 text-[32px] font-bold leading-none tracking-tight text-foreground tabular-nums">
        {display}
      </p>

      <p className="mt-2.5 flex items-center gap-1 text-[11px]">
        {up || down ? (
          <>
            <span aria-hidden="true" className={up ? deltaColor : "text-muted-foreground"}>
              {up ? (
                <ArrowUpRightIcon className="h-3 w-3" />
              ) : (
                <ArrowDownRightIcon className="h-3 w-3" />
              )}
            </span>
            <span
              className={cn(
                "font-semibold tabular-nums",
                up ? deltaColor : "text-muted-foreground"
              )}
            >
              {up ? "+" : ""}
              {delta}
            </span>
            <span className="text-muted-foreground">{period}</span>
          </>
        ) : (
          // A zero delta keeps its figure honest — the em dash the approved
          // mockup gives the Analyzed card — followed by "no change {period}".
          <span className="text-muted-foreground">
            — no change {period}
          </span>
        )}
      </p>
    </motion.div>
  );
}

/**
 * The loading twin — same box the real card occupies: the tinted fill, the
 * one-step border, the icon slot, the 32px figure, the trend line, all in
 * place so nothing moves when data lands.
 *
 * The TONE is passed, not fetched: the dashboard's three cards are always
 * blue/amber/neutral by position (TYPE_META is static), so the skeleton can
 * carry the card's color identity before any data arrives — a claim about
 * layout slot, not about a value. Bars inside keep the app's skeleton recipe
 * (--muted-foreground/20); on a tinted card that steps up slightly in
 * contrast, which is why the skeleton's bars are the fill's own hue (the
 * tile-*-fg token) at low alpha rather than the grey recipe.
 */
const TONE_SKELETON: Record<StatTone, string> = {
  blue: "border-tile-blue-border bg-tile-blue-bg",
  amber: "border-tile-amber-border bg-tile-amber-bg",
  neutral: "border-tile-neutral-border bg-tile-neutral-bg",
};

const TONE_SKELETON_FG: Record<StatTone, string> = {
  blue: "bg-tile-blue-fg/20",
  amber: "bg-tile-amber-fg/20",
  neutral: "bg-tile-neutral-fg/20",
};

export function StatCardSkeleton({
  tone = "neutral",
  reduced = false,
}: {
  tone?: StatTone;
  reduced?: boolean;
}) {
  const pulse = !reduced;

  return (
    <div
      data-testid="stat-card-skeleton"
      className={cn(
        "flex flex-col rounded-card border p-4",
        TONE_SKELETON[tone]
      )}
    >
      <div className="flex items-center gap-1.5">
        <div className={cn("h-3.5 w-3.5 flex-none rounded-sm", TONE_SKELETON_FG[tone], pulse && "animate-pulse")} />
        <div className={cn("h-3 w-20 rounded-pill", TONE_SKELETON_FG[tone], pulse && "animate-pulse")} />
      </div>
      <div className={cn("mt-2.5 h-8 w-14 rounded-md", TONE_SKELETON_FG[tone], pulse && "animate-pulse")} />
      <div className={cn("mt-2.5 h-3 w-28 rounded-pill", TONE_SKELETON_FG[tone], pulse && "animate-pulse")} />
    </div>
  );
}
